import { cleanDiacritics, uuidv4 } from '@vindr/utils/helpers/common.helper';
import { Checkbox, Collapse, Form, Input } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Panel } = Collapse;

interface PermissionsProps {
  name?: string;
  permissions: Array<{
    group: string;
    permissions: Array<string>;
  }>;
  open: boolean;
}

const unHidePermissionsList = (permissions: Array<any>) => {
  return permissions ? permissions.map((item) => ({ permission: item, hide: false })) : [];
};

const Permissions = ({ name = 'permissions', permissions, open, ...props }: PermissionsProps) => {
  const { t } = useTranslation('Vindoc');
  const groupsRef = useRef(null);
  const [inputVal, setInputVal] = useState(null);

  const unHidePermissions = useMemo(() => {
    if (!permissions) return [];

    return permissions.map((item) => ({
      ...item,
      permissions: unHidePermissionsList(item.permissions),
      hide: false,
    }));
  }, [`${permissions}`]);

  const [searchData, setSearchData] = useState([]);
  const [hideAll, setHideAll] = useState(true);

  const onSearch = useCallback((value: string) => {
    setInputVal(value);
    if (value === '') {
      setSearchData(unHidePermissions);
      setHideAll(false);

      return;
    }

    value = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); //? Convert special characters regex to string
    const searchRegex = new RegExp(`(${value}|${t(value)}).*`, 'ig');

    const data = [];

    for (const items of unHidePermissions) {
      if (
        cleanDiacritics(items.group).match(searchRegex) ||
        cleanDiacritics(t(items.group)).match(searchRegex)
      ) {
        data.push(items);
        continue;
      }

      let hideAll = true;

      const permissions = items.permissions.map(({ permission }) => {
        if (
          cleanDiacritics(permission).match(searchRegex) ||
          cleanDiacritics(t(permission)).match(searchRegex)
        ) {
          hideAll = false;

          return { permission, hide: false };
        }

        return { permission, hide: true };
      });

      data.push({ group: items.group, permissions, hide: hideAll });
    }

    setHideAll(data.every((item) => item.hide));
    setSearchData(data);
  }, []);

  useEffect(() => {
    setSearchData(unHidePermissions);
    onSearch('');
  }, [`${unHidePermissions}`, `${permissions}`]);

  useEffect(() => {
    if (open) {
      setInputVal('');
      onSearch('');
    }
  }, [open]);

  if (!permissions) return <></>;

  return (
    <section>
      <p style={{ paddingBottom: 8 }}>{t('Permissions')}</p>
      <div className="permission-list" ref={groupsRef}>
        <Form.Item
          {...(hideAll ? { validateStatus: 'error' } : {})}
          {...(hideAll ? { help: t('No permission found') } : {})}
        >
          <Input
            placeholder={t('Search')}
            value={inputVal}
            onChange={(e) => onSearch(e.target.value)}
            className="search-permission"
            allowClear
          />
        </Form.Item>
        <Form.Item name={name}>
          <Checkbox.Group className="group-permission mt-2 max-h-[480px] w-full" {...props}>
            <Collapse
              className="permission w-full overflow-y-auto"
              defaultActiveKey={unHidePermissions?.map((item) => item.group)}
            >
              {searchData.map((permissionGroup) => (
                <Panel
                  header={t(permissionGroup.group)}
                  key={permissionGroup.group}
                  className={permissionGroup.hide ? 'hidden' : ''}
                >
                  {permissionGroup.permissions?.map(({ permission, hide }) => (
                    <div key={`${permission} ${uuidv4()}`} className="permission" hidden={hide}>
                      <Checkbox value={permission}>
                        <span>{permission}</span>
                      </Checkbox>
                    </div>
                  ))}
                </Panel>
              ))}
            </Collapse>
          </Checkbox.Group>
        </Form.Item>
      </div>
    </section>
  );
};

export default Permissions;
